import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, TrendingUp, DollarSign, BarChart3 } from 'lucide-react';
import { axiosInstance } from '@/App';
import {
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area
} from 'recharts';

// Custom tooltip for spend/leads chart - defined outside component
const SpendLeadsTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
        <p className="font-semibold mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.name === 'Ausgaben' ? `${entry.value.toFixed(2)}€` : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Custom tooltip for efficiency chart - defined outside component
const EfficiencyTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
        <p className="font-semibold mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.name === 'CTR' ? `${entry.value.toFixed(2)}%` : `${entry.value.toFixed(2)}€`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const PerformanceCharts = ({ adAccountId, datePreset = 'last_7d' }) => {
  const [loading, setLoading] = useState(false);
  const [dailyData, setDailyData] = useState([]);
  const [chartDatePreset, setChartDatePreset] = useState(datePreset);

  const datePresetOptions = [
    { value: 'last_7d', label: 'Letzte 7 Tage' },
    { value: 'last_14d', label: 'Letzte 14 Tage' },
    { value: 'last_30d', label: 'Letzte 30 Tage' },
    { value: 'this_month', label: 'Dieser Monat' },
    { value: 'last_month', label: 'Letzter Monat' }
  ];

  const loadDailyInsights = useCallback(async () => {
    if (!adAccountId) return;
    
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/meta/insights/daily?ad_account_id=${adAccountId}&date_preset=${chartDatePreset}`);
      if (response.data && response.data.daily_data) {
        // Format dates for display
        const formattedData = response.data.daily_data.map(day => ({
          ...day,
          dateLabel: new Date(day.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
          // Ensure numeric values
          spend: parseFloat(day.spend) || 0,
          leads: parseInt(day.leads) || 0,
          ctr: parseFloat(day.ctr) || 0,
          cpc: parseFloat(day.cpc) || 0,
          cpl: parseFloat(day.cpl) || 0,
          clicks: parseInt(day.clicks) || 0
        }));
        setDailyData(formattedData);
      }
    } catch (error) {
      console.error('Failed to load daily insights:', error);
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  }, [adAccountId, chartDatePreset]);

  useEffect(() => {
    if (adAccountId) {
      loadDailyInsights();
    }
  }, [adAccountId, chartDatePreset, loadDailyInsights]);

  if (!adAccountId) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="performance-charts">
      {/* Header with date selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          Performance-Trends
        </h3>
        <Select value={chartDatePreset} onValueChange={setChartDatePreset}>
          <SelectTrigger className="w-[160px]" data-testid="chart-date-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {datePresetOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            <span className="ml-2 text-gray-600">Lade Performance-Daten...</span>
          </div>
        </Card>
      ) : dailyData.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="font-medium">Keine Performance-Daten verfügbar</p>
          <p className="text-sm">Starten Sie eine Kampagne, um Daten zu sammeln</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Spend & Leads Chart */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-2 bg-gradient-to-r from-blue-50 to-purple-50">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-blue-600" />
                Ausgaben & Leads
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="dateLabel" 
                    tick={{ fontSize: 11 }}
                    tickMargin={8}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `${value}€`}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right"
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip content={<SpendLeadsTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="spend"
                    name="Ausgaben"
                    fill="#3b82f6"
                    fillOpacity={0.2}
                    stroke="#3b82f6"
                    strokeWidth={2}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="leads"
                    name="Leads"
                    fill="#8b5cf6"
                    radius={[4, 4, 0, 0]}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Efficiency Metrics Chart */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                Effizienz-Metriken
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="dateLabel" 
                    tick={{ fontSize: 11 }}
                    tickMargin={8}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => value.toFixed(1)}
                  />
                  <Tooltip content={<EfficiencyTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line
                    type="monotone"
                    dataKey="ctr"
                    name="CTR"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="cpc"
                    name="CPC"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ fill: '#f59e0b', r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="cpl"
                    name="CPL"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={{ fill: '#ef4444', r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PerformanceCharts;
